import * as Sentry from "@sentry/react";
import { ScoreEmailPayload, WelcomeEmailPayload, ExportSuccessPayload, ExportErrorPayload, ShopifySyncSuccessPayload, ShopifySyncFailedPayload } from "./types";


class Mailer {
  private static instance: Mailer;
  private readonly baseUnsubscribeUrl: string = 'https://app.marketplaceiq.io/unsubscribe';

  private constructor() {
    
  }

  private generateUnsubscribeLink(email: string): string {
    if (!email) {
      throw new Error('Email is required for unsubscribe link generation');
    }
    const encodedEmail = encodeURIComponent(email);
    return `${this.baseUnsubscribeUrl}?email=${encodedEmail}`;
  }

  public static getInstance(): Mailer {
    if (!Mailer.instance) {
      Mailer.instance = new Mailer();
    }
    return Mailer.instance;
  }

  public async sendWelcomeEmail(payload: Pick<WelcomeEmailPayload, "Email">): Promise<void> {
    const webhookUrl = 'https://hooks.zapier.com/hooks/catch/15763147/2ihk7b1';
    const modifiedPayload: WelcomeEmailPayload = {
      Name: payload.Email,
      Company: '',
      phone: '',
      position: '',
      department: '',
      location: '',
      subscription_type: '',
      ...payload,
      signup_date: new Date().toISOString().split('T')[0],
    }
    try {
      await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(modifiedPayload),
      });
      console.log('Welcome email sent for email:', payload.Email);
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error sending email:', error);
    }
  }

  public async sendScoreEmail(payload: ScoreEmailPayload): Promise<void> {
    const webhookUrl = 'https://hooks.zapier.com/hooks/catch/15763147/2z2ni9p/';
    const modifiedPayload = {
      ...payload,
      UNSUBSCRIBE_LINK: this.generateUnsubscribeLink(payload.email_to)
    };

    try {
      await fetch(webhookUrl, {
        method: 'POST',
        mode: "no-cors",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(modifiedPayload),
      });
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error sending email:', error);
    }
  }

  public async sendExportSuccessEmail(payload: ExportSuccessPayload): Promise<void> {
    const webhookUrl = 'https://hooks.zapier.com/hooks/catch/15763147/2lk7rs1/';
    const modifiedPayload = {
      company_name: payload.company_name,
      number_of_products: payload.number_of_products,
      score_improvement: payload.score_improvement, 
      email: payload.email,
      subject_line: `${payload.company_name}: Your MarketplaceIQ Content is Ready! ✨`
    };

    try {
      await fetch(webhookUrl, {
        method: 'POST',
        mode: "no-cors",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(modifiedPayload),
      });
      console.log('Export success email sent for:', payload.email);
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error sending export success email:', error);
    }
  }

  public async sendExportErrorEmail(payload: ExportErrorPayload): Promise<void> {
    const webhookUrl = 'https://hooks.zapier.com/hooks/catch/15763147/2lkvq9i/';
    const modifiedPayload = {
      company_name: payload.company_name,
      email: payload.email,
      error_type: payload.error_type,
      subject_line: "Action Required: Issue with Your MarketplaceIQ Job"
    };

    try {
      await fetch(webhookUrl, {
        method: 'POST',
        mode: "no-cors",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(modifiedPayload),
      });
      console.log('Export error email sent for:', payload.email);
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error sending export error email:', error);
    }
  }

  public async sendShopifySyncSuccessEmail(payload: ShopifySyncSuccessPayload): Promise<void> {
    const webhookUrl = 'https://hooks.zapier.com/hooks/catch/15763147/2lkjxbt/';
    const modifiedPayload = {
      company_name: payload.company_name,
      number_of_products: payload.number_of_products,
      email: payload.email,
      subject_line: payload.subject_line || `${payload.company_name}: Products Successfully Synced to Shopify`,
      template: payload.template || "sync"
    };

    try {
      await fetch(webhookUrl, {
        method: 'POST',
        mode: "no-cors",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(modifiedPayload),
      });
      console.log('Shopify sync success email sent for:', payload.email);
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error sending shopify sync success email:', error);
    }
  }

  public async sendShopifySyncFailedEmail(payload: ShopifySyncFailedPayload): Promise<void> {
    const webhookUrl = 'https://hooks.zapier.com/hooks/catch/15763147/2lkvsd3/';
    const modifiedPayload = {
      company_name: payload.company_name,
      number_of_products: payload.number_of_products,
      email: payload.email,
      subject_line: payload.subject_line || `${payload.company_name}: Shopify Sync Issue Detected`,
      sync_status: payload.sync_status || "failed"
    };

    try {
      await fetch(webhookUrl, {
        method: 'POST',
        mode: "no-cors",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(modifiedPayload),
      });
      console.log('Shopify sync failed email sent for:', payload.email);
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error sending shopify sync failed email:', error);
    }
  }
}

export const mailer = Mailer.getInstance();
